import { useEffect, useState } from "react"
import { Redirect, router } from "expo-router"
import { Pressable, View } from "react-native"
import {
  useAppleLoginRegisterMutation,
  useCheckAccountExists,
  useGoogleLoginAuthMutation,
  useGoogleLoginRegisterMutation,
  useRegistrationMutation,
} from "@/queries/user"
import ConfirmCheckedState from "@/assets/icons/confirm-checked-state.svg"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { GOOGLE_SCOPES } from "@/constants/Scopes"
import { showErrorToast } from "@/components/toasts/AppToast"
import { useStore } from "@nanostores/react"
import { $refCode } from "@/stores/refCode"

import { useRegistrationInputs } from "@/stores/register"

import * as registerEvents from "@/analytics/events/register"
import { cn } from "@/components/util/cn"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { useUtmStore } from "@/stores/utm"
import Head from "expo-router/head"
import BFText, { H6, Tiny } from "@/components/standard/BFText"
import { A } from "@/components/standard/Html"
import {
  $preQual,
  useDidPreQual,
  usePreQualStateAsLoanProfile,
} from "@/stores/preQual"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { TextFieldWithLabel } from "@/components/forms/FieldWithLabel"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Image } from "expo-image"
import { Masks } from "react-native-mask-input"
import { Sentry } from "@/hooks/sentry"
import { fbqTrackCustom, fbqUserAttributes } from "@/utils/PixelUtils"

interface ConfirmSignUpProps {
  firstName?: string
  lastName?: string
  phoneNumber?: string
}

const ConfirmSignUpForm: React.FC<ConfirmSignUpProps> = ({
  firstName,
  lastName,
  phoneNumber,
}: ConfirmSignUpProps) => {
  useEffect(() => {
    registerEvents.confirmRegistrationViewed()
  }, [])

  const isDesktop = useIsDesktop()

  const {
    setFirstName,

    setLastName,

    setPhoneNumber,
  } = useRegistrationInputs()

  return (
    <>
      <View className={cn("flex flex-col gap-5", !isDesktop && "flex-1")}>
        <TextFieldWithLabel
          label={"First Name"}
          dark={true}
          required={true}
          validator={(value) =>
            value.length < 1 ? "First name is required" : ""
          }
          initialValue={firstName}
          onValueChange={setFirstName}
          textFieldProps={{
            returnKeyType: "done",
            testID: "first-name-text-input",
          }}
        />
        <TextFieldWithLabel
          label={"Last Name"}
          initialValue={lastName}
          validator={(value) =>
            value.length < 1 ? "Last name is required" : ""
          }
          onValueChange={setLastName}
          dark={true}
          required={true}
          textFieldProps={{
            returnKeyType: "done",
            testID: "last-name-text-input",
          }}
        />
        <TextFieldWithLabel
          label={"Phone Number"}
          dark={true}
          required={false}
          initialValue={phoneNumber || ""}
          validator={(value) =>
            value.length !== 0 && value.length !== 10
              ? "Invalid phone number"
              : undefined
          }
          onValueChange={setPhoneNumber}
          mask={Masks.USA_PHONE}
          textFieldProps={{
            keyboardType: "phone-pad",
            placeholder: "(___) ___-____",
            testID: "phone-text-input",
          }}
        />
        <View className="flex flex-col flex-1 px-[5px] justify-start">
          <Tiny className="text-white">
            By providing your phone number, you consent to receive text messages
            from us.
          </Tiny>
        </View>
      </View>
    </>
  )
}

const AgreeCheckBox = ({
  checked = false,
  onPress = () => {},
  error = null,
}: {
  checked?: boolean
  onPress?: () => void
  error?: string | null
}) => {
  return (
    <View className="flex flex-row items-center justify-center p-[10px] h-[40px]">
      <View className="flex flex-row items-center justify-start gap-[10px] h-[20px]">
        <Pressable
          testID="terms-agree-pressable"
          onPress={onPress}
          hitSlop={24}
        >
          {checked ? (
            <ConfirmCheckedState />
          ) : (
            <View
              className={`border size-[20px] ${error ? "border-salmon" : "border-white"}`}
            />
          )}
        </Pressable>
        <View className="relative">
          <BFText className="text-white font-spacegrotesk leading-3 text-[10px]">
            <BFText onPress={onPress}>I agree to Backflip&apos;s&nbsp;</BFText>
            <A
              href="https://dobackflip.com/backflip-terms-of-use-agreement/"
              className="underline text-teal"
              onPress={() => {
                registerEvents.termsOfUseClicked({
                  url: "https://dobackflip.com/backflip-terms-of-use-agreement/",
                })
              }}
            >
              Terms of Use
            </A>
            <BFText> and </BFText>
            <A
              href="https://dobackflip.com/privacy/"
              className="underline text-teal"
              onPress={() => {
                registerEvents.privacyPolicyClicked({
                  url: "https://dobackflip.com/privacy/",
                })
              }}
            >
              Privacy Policy.
            </A>
          </BFText>
          {error && (
            <BFText className="text-salmon font-spacegrotesk leading-3 text-[10px] absolute bottom-0 left-0 mb-[-14px]">
              {error}
            </BFText>
          )}
        </View>
      </View>
    </View>
  )
}

export default function RegisterConfirm() {
  const registrationMutation = useRegistrationMutation()
  const checkAccountExists = useCheckAccountExists()
  const googleLoginRegisterMutation = useGoogleLoginRegisterMutation()
  const googleLoginAuthMutation = useGoogleLoginAuthMutation()
  const appleLoginRegisterMutation = useAppleLoginRegisterMutation()
  const insets = useSafeAreaInsets()
  const { isAndroid } = useDeviceAttributes()
  const [loading, setLoading] = useState(false)

  const {
    registerInputs,
    setFirstNameError,
    setLastNameError,
    setEmailError,
    validateConfimSignUpInputs,
    confirmationMode,
    setConfirmationMode,
    confirmAgreeToTerms,
    setConfirmAgreeToTerms,
    confirmAgreeToTermsError,
    googleCode,
    appleCode,
  } = useRegistrationInputs()

  useViewedCallback(() => {
    setStatusBarStyle("light")
  })

  const { firstName, lastName, email, phoneNumber, password } = registerInputs

  const refCode = useStore($refCode)
  const utm = useUtmStore()
  const preQualState = usePreQualStateAsLoanProfile()
  const isPreQualFlow = useDidPreQual()

  const hasPendingMutations =
    registrationMutation.isPending ||
    googleLoginAuthMutation.isPending ||
    googleLoginRegisterMutation.isPending ||
    checkAccountExists.isPending

  const onGoogleSignUp = () => {
    const phone = phoneNumber?.replace(/\D/g, "") || null
    if (!googleCode) {
      return
    }

    if (validateConfimSignUpInputs()) {
      registerEvents.registrationConfirmSubmitted({
        firstName,
        lastName,
        phoneNumber,
        preQual: isPreQualFlow ? preQualState : undefined,
      })
      googleLoginRegisterMutation.mutate(
        {
          serviceType: "register",
          idToken: googleCode.idToken,
          code: googleCode.serverAuthCode || null,
          scopes: GOOGLE_SCOPES,
          redirectUri: googleCode.redirectUri!,
          firstName,
          lastName,
          phoneNumber: phone,
          refCode,
          utm,
          preQual: isPreQualFlow ? preQualState : null,
        },
        {
          onSuccess: () => {
            console.log("Registration successful")
          },
          onError: (error) => {
            // TODO: Uhm this seems to just silently fail here..
            console.log(error)
          },
        }
      )
    }
  }

  const onAppleSignUp = () => {
    const phone = phoneNumber === "" ? null : phoneNumber?.replace(/\D/g, "")
    if (!appleCode) {
      return
    }

    if (validateConfimSignUpInputs()) {
      registerEvents.registrationConfirmSubmitted({
        firstName,
        lastName,
        phoneNumber,
      })
      appleLoginRegisterMutation.mutate(
        {
          serviceType: "register",
          idToken: appleCode.identityToken || "",
          firstName,
          lastName,
          phoneNumber: phone,
          refCode,
          utm,
        },
        {
          onSuccess: () => {
            console.log("Registration successful")
          },
          onError: (error) => {
            console.log(error)
          },
        }
      )
    }
  }

  const onEmailSignUp = async () => {
    const phone = phoneNumber === "" ? null : phoneNumber?.replace(/\D/g, "")

    if (!validateConfimSignUpInputs()) {
      showErrorToast({
        title: "Validation Error",
        message: "Make sure all required fields are filled in then try again.",
        autoHide: true,
        visibilityTime: 2000,
      })
      return
    }

    registerEvents.registrationConfirmSubmitted({
      firstName,
      lastName,
      phoneNumber,
      preQual: isPreQualFlow ? preQualState : undefined,
    })

    setLoading(true)
    registrationMutation.mutate(
      {
        firstName,
        lastName,
        email,
        phoneNumber: phone,
        password,
        refCode,
        utm,
        preQual: isPreQualFlow ? preQualState : null,
      },
      {
        onSuccess: () => {
          setLoading(false)
          if (isPreQualFlow) {
            const fbqAttributes = fbqUserAttributes($preQual.get(), null)
            fbqAttributes.email = email
            fbqTrackCustom("CompletedPreQualFlow", fbqAttributes)
          }
          console.log("Registration successful")
        },
        onError: (error) => {
          setLoading(false)
          Sentry.captureMessage("Error registering user", {
            extra: {
              firstName,
              lastName,
              email,
              phoneNumber,
              password: password.replace(/./g, "*"),
              refCode,
              utm,
              preQual: isPreQualFlow ? preQualState : null,
            },
          })
          Sentry.captureException(error)
          const errorResponse = (error as any)?.response
          if (errorResponse?.errors) {
            const errorMessage = errorResponse.errors[0].message

            if (errorMessage === "REGISTER_EMAIL_EXISTS") {
              setEmailError("Email address is already registered.")
              setConfirmationMode("initial")
              router.replace("/register/")
              return
            }
            if (errorMessage === "REGISTER_FULL_NAME_REQUIRED") {
              setFirstNameError("Full name is required.")
              setLastNameError("Full name is required.")
            } else {
              showErrorToast({
                message: errorMessage,
              })
            }
          }

          showErrorToast({
            title: "Server Error",
            message:
              "An error occurred while registering. Please try again in a minute.",
          })
        },
      }
    )
  }

  const onSignUp = () => {
    switch (confirmationMode) {
      case "email":
        void onEmailSignUp()
        break

      case "google":
        onGoogleSignUp()
        break

      case "apple":
        onAppleSignUp()
        break

      default:
        // We're getting some silent button not working issues on this screen.
        // This seems to be the only place where we're not handling the case where
        // the user has not selected a confirmation mode (which is in theory not possible).
        // TODO: Just make email default, but for now capture errors to see what is happening first.
        Sentry.captureException(
          new Error(
            `Invalid confirmation mode ${confirmationMode}. Assuming Email.`
          )
        )
        void onEmailSignUp()
    }
  }

  if (!email && !googleCode && !appleCode) {
    return <Redirect href={"/register"} />
  }

  return (
    <>
      <View className={"shrink flex-1"}>
        <Head>
          <title>Backflip - Sign Up</title>
        </Head>
        <Horizontal className={"bg-midnight-blue my-5 gap-5"}>
          <Image
            source={require("@/assets/images/ad-person.png")}
            style={{ width: 65, height: 65 }}
          />
          <Vertical className={"flex-1 justify-center items-start py-2"}>
            <H6 className="flex-1 text-white">
              “The way Backflip moves is different, like a partner–not a
              lender.”
            </H6>
            <Tiny className="text-white">
              - Jay Donaldson, Don’s Property LLC
            </Tiny>
          </Vertical>
        </Horizontal>
        <ConfirmSignUpForm
          firstName={firstName}
          lastName={lastName}
          phoneNumber={phoneNumber || undefined}
        />
      </View>
      <View className={cn(`items-center flex flex-col justify-end shrink-0`)}>
        <View className="w-full pb-[15px] flex flex-row justify-center">
          <AgreeCheckBox
            checked={confirmAgreeToTerms}
            error={confirmAgreeToTermsError}
            onPress={() => {
              const newConfirmValue = !confirmAgreeToTerms
              setConfirmAgreeToTerms(newConfirmValue)
              registerEvents.termsAndConditionsClicked({
                action: newConfirmValue ? "select" : "deselect",
              })
            }}
          />
        </View>
      </View>
      <View
        className="w-full shrink-0"
        style={{ marginBottom: insets.bottom + (isAndroid ? 60 : 0) }}
      >
        <PrimaryDarkButton
          testID="sign-up-button"
          loading={loading}
          title={
            confirmationMode === "initial" || !isPreQualFlow
              ? "Sign Up"
              : "See Loan Options"
          }
          onPress={onSignUp}
          disabled={hasPendingMutations}
        />
      </View>
    </>
  )
}
